import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useStore } from '../../context/StoreContext';
import { Card } from '../../components/ui/Card';
import { exportJson } from '../../lib/exportUtils';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiCheckCircle, FiClock, FiUserPlus, FiHeart, FiTrendingUp, FiDownload, FiMap } = FiIcons;

export const Dashboard = () => {
  const { currentEvent } = useStore();
  const [stats, setStats] = useState(null);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  useEffect(() => {
    if (currentEvent && currentEvent.event_id) {
      const fetchStats = async () => {
        const data = await api.getDashboardStats(currentEvent.event_id);
        setStats(data);
      };
      
      fetchStats();
      const unsubscribe = api.subscribeToRegistrations(currentEvent.event_id, fetchStats);
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [currentEvent]);

  const handleQuickBackup = async () => {
    setIsBackingUp(true);
    setStatusMsg(null);
    try {
      const data = await api.getFullBackup();
      exportJson(data, 'Padua_Midsummer_FULL_BACKUP');
      setStatusMsg({ type: 'success', text: 'Backup downloaded successfully.' });
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Backup could not be created.' });
    } finally {
      setIsBackingUp(false);
      setTimeout(() => setStatusMsg(null), 4000);
    }
  };

  if (!stats) return <div className="text-center py-12">Loading metrics...</div>;

  const totalTourResponses = stats.tourNever + stats.tourScheduled + stats.tourPreviously + stats.tourUnknown;

  return (
    <div className="space-y-8 animate-in fade-in">
      {statusMsg && (
        <div className={`p-4 rounded-xl text-sm font-medium ${statusMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {statusMsg.text}
        </div>
      )}
      
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-cinzel text-padua-green">Live Event Dashboard</h1>
          <p className="text-gray-500 mt-1">Real-time local iPad metrics</p>
        </div>
        <button 
          onClick={handleQuickBackup}
          disabled={isBackingUp}
          className="flex items-center text-sm font-medium text-padua-gold hover:text-padua-gold/80 transition-colors bg-padua-gold/10 px-4 py-2 rounded-lg disabled:opacity-50"
        >
          <FiDownload className="mr-2" />
          {isBackingUp ? 'Exporting...' : 'BACKUP NOW'}
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="RSVP Parties" value={stats.totalRSVPs} icon={<FiUsers />} color="bg-blue-50 text-blue-600" />
        <StatCard title="Expected Headcount" value={stats.expectedHeadcount} icon={<FiUsers />} color="bg-blue-50 text-blue-600" />
        
        <StatCard title="Checked-In Parties" value={stats.checkedIn} icon={<FiCheckCircle />} color="bg-green-50 text-green-600" />
        <StatCard title="Arrived Headcount" value={stats.totalHeadcount} icon={<FiHeart />} color="bg-padua-gold/20 text-padua-gold" />
        
        <StatCard title="Not Arrived (Parties)" value={stats.notArrived} icon={<FiClock />} color="bg-orange-50 text-orange-600" />
        <StatCard title="Walk-Ins (Parties)" value={stats.walkIns} icon={<FiUserPlus />} color="bg-purple-50 text-purple-600" />
        
        <StatCard title="Check-In Rate" value={`${stats.attendanceRate}%`} icon={<FiTrendingUp />} color="bg-gray-100 text-gray-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl">
        <Card className="p-6">
          <h2 className="text-xl font-cinzel text-padua-green mb-6 flex items-center"><FiHeart className="mr-2"/> Checked-In Audience</h2>
          <div className="space-y-4">
            <ProgressBar label="Booked Padua Couples" value={stats.booked} total={stats.checkedIn} color="bg-padua-gold" />
            <ProgressBar label="Prospective Couples" value={stats.prospective} total={stats.checkedIn} color="bg-padua-green" />
            <ProgressBar label="Guests / Family / Other" value={stats.other} total={stats.checkedIn} color="bg-padua-sage" />
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-cinzel text-padua-green mb-6 flex items-center"><FiMap className="mr-2"/> Checked-In Tour Status</h2>
          <div className="space-y-4">
            <ProgressBar 
              label="Never Toured" 
              value={stats.tourNever} 
              total={totalTourResponses} 
              color="bg-blue-500" 
            />
            <ProgressBar 
              label="Tour Scheduled" 
              value={stats.tourScheduled} 
              total={totalTourResponses} 
              color="bg-purple-500" 
            />
            <ProgressBar 
              label="Previously Toured" 
              value={stats.tourPreviously} 
              total={totalTourResponses} 
              color="bg-teal-500" 
            />
            <ProgressBar 
              label="Not Sure" 
              value={stats.tourUnknown} 
              total={totalTourResponses} 
              color="bg-gray-400" 
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => (
  <Card className="p-6 flex flex-col items-center justify-center text-center">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${color}`}>
      {React.cloneElement(icon, { className: 'text-2xl' })}
    </div>
    <p className="text-3xl font-bold text-gray-800 mb-1">{value}</p>
    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{title}</p>
  </Card>
);

const ProgressBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500">{value} ({Math.round(percentage)}%)</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full ${color}`} style={{ width: `${percentage}%` }}></div>
      </div>
    </div>
  );
};